import { useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Image as ImageIcon,
  ImagePlus,
  Loader2,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FolderUploadButton } from '@/components/ui/FolderUploadButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { EditableText } from '@/components/inventory/EditableItemField';
import { RemoveIconButton } from '@/components/equipment/DetailRows';
import {
  useDeleteEquipmentFile,
  useEquipmentFileUrl,
  useEquipmentFiles,
  useRenameEquipmentFile,
  useSetEquipmentCover,
  useUploadEquipmentFile,
  type Equipment,
  type EquipmentFile,
} from '@/features/equipment/hooks';
import { MAX_EQUIPMENT_PHOTO_BYTES } from '@/features/equipment/model';
import { formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function EquipmentPhotosTab({
  equipment,
  canEdit,
}: {
  equipment: Equipment;
  canEdit: boolean;
}) {
  const { data: files, isLoading } = useEquipmentFiles(equipment.id);
  const upload = useUploadEquipmentFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [viewIndex, setViewIndex] = useState<number | null>(null);

  const photos = useMemo(() => (files ?? []).filter((f) => f.kind === 'photo'), [files]);

  const handleFiles = async (list: FileList | File[] | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    let needsCover = !equipment.image_path;
    let uploaded = 0;
    try {
      for (const file of Array.from(list)) {
        if (!file.type.startsWith('image/')) {
          toast.error(`“${file.name}” isn't an image.`);
          continue;
        }
        if (file.size > MAX_EQUIPMENT_PHOTO_BYTES) {
          toast.error(`“${file.name}” is over 15 MB.`);
          continue;
        }
        await upload.mutateAsync({
          equipmentId: equipment.id,
          file,
          kind: 'photo',
          makeCover: needsCover,
        });
        needsCover = false;
        uploaded += 1;
      }
      if (uploaded > 0) toast.success(uploaded === 1 ? 'Photo uploaded' : `${uploaded} photos uploaded`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const viewing = viewIndex != null ? photos[viewIndex] : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Photos</CardTitle>
          {canEdit && (
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ImagePlus className="h-3.5 w-3.5" />
                )}
                {uploading ? 'Uploading…' : 'Add photos'}
              </Button>
              <FolderUploadButton
                size="sm"
                accept="image/*"
                disabled={uploading}
                onFiles={(files) => void handleFiles(files)}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square w-full rounded-md" />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <EmptyState
            icon={ImageIcon}
            title="No photos yet"
            action={
              canEdit ? (
                <Button variant="outline" onClick={() => inputRef.current?.click()}>
                  <ImagePlus className="h-4 w-4" />
                  Add photos
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((photo, i) => (
              <PhotoTile
                key={photo.id}
                photo={photo}
                isCover={photo.storage_path === equipment.image_path}
                coverPath={equipment.image_path}
                canEdit={canEdit}
                onOpen={() => setViewIndex(i)}
              />
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={viewing != null} onOpenChange={(open) => !open && setViewIndex(null)}>
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto" dismissOnOutsideClick>
          {viewing && (
            <PhotoViewer
              photo={viewing}
              position={`${(viewIndex ?? 0) + 1} of ${photos.length}`}
              onPrev={
                photos.length > 1
                  ? () => setViewIndex((i) => ((i ?? 0) - 1 + photos.length) % photos.length)
                  : undefined
              }
              onNext={
                photos.length > 1
                  ? () => setViewIndex((i) => ((i ?? 0) + 1) % photos.length)
                  : undefined
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PhotoTile({
  photo,
  isCover,
  coverPath,
  canEdit,
  onOpen,
}: {
  photo: EquipmentFile;
  isCover: boolean;
  coverPath: string | null;
  canEdit: boolean;
  onOpen: () => void;
}) {
  const { data: url, isLoading } = useEquipmentFileUrl(photo.storage_path);
  const rename = useRenameEquipmentFile();
  const remove = useDeleteEquipmentFile();
  const setCover = useSetEquipmentCover();

  const onDelete = async () => {
    if (!window.confirm(`Delete “${photo.label}”? This can't be undone.`)) return;
    try {
      await remove.mutateAsync({ file: photo, coverPath });
      toast.success('Photo deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete photo');
    }
  };

  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={onOpen}
        className="relative block aspect-square w-full bg-secondary/30"
        aria-label={`View ${photo.label}`}
      >
        {isLoading || !url ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : (
          <img src={url} alt={photo.label} loading="lazy" className="h-full w-full object-cover" />
        )}
        {isCover && (
          <Badge variant="success" className="absolute left-2 top-2 gap-1">
            <Star className="h-3 w-3" />
            Cover
          </Badge>
        )}
      </button>
      <div className="space-y-1 p-2">
        <EditableText
          value={photo.label}
          required
          maxLength={200}
          ariaLabel="Photo name"
          canEdit={canEdit}
          density="cell"
          triggerClassName="text-xs"
          onSave={async (next) => {
            await rename.mutateAsync({ id: photo.id, equipmentId: photo.equipment_id, label: next });
          }}
        />
        <div className="flex items-center justify-between gap-1">
          <span
            className={cn(
              'truncate text-[11px] text-muted-foreground',
              canEdit && 'hidden sm:block',
            )}
          >
            {formatDate(photo.created_at)}
          </span>
          {canEdit && (
            <div className="ml-auto flex shrink-0 items-center">
              {!isCover && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-9 px-2 text-xs"
                  disabled={setCover.isPending}
                  onClick={async () => {
                    try {
                      await setCover.mutateAsync({
                        equipmentId: photo.equipment_id,
                        path: photo.storage_path,
                      });
                      toast.success('Cover photo updated');
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : 'Could not set cover');
                    }
                  }}
                >
                  Set cover
                </Button>
              )}
              <RemoveIconButton
                label={`Delete ${photo.label}`}
                onClick={() => void onDelete()}
                disabled={remove.isPending}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoViewer({
  photo,
  position,
  onPrev,
  onNext,
}: {
  photo: EquipmentFile;
  position: string;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const { data: url, isLoading } = useEquipmentFileUrl(photo.storage_path);
  return (
    <div className="space-y-3">
      <div className="pr-8">
        <DialogTitle className="truncate">{photo.label}</DialogTitle>
        <DialogDescription>
          {position} · Added {formatDate(photo.created_at)}
        </DialogDescription>
      </div>
      <div className="relative flex min-h-[40vh] items-center justify-center rounded-md bg-secondary/30">
        {isLoading || !url ? (
          <Skeleton className="h-[60vh] w-full" />
        ) : (
          <img src={url} alt={photo.label} className="max-h-[70vh] w-auto object-contain" />
        )}
        {onPrev && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-card/90"
            onClick={onPrev}
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        {onNext && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-card/90"
            onClick={onNext}
            aria-label="Next photo"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        )}
      </div>
      {url && (
        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            <a href={url} target="_blank" rel="noreferrer" download={photo.label}>
              <Download className="h-4 w-4" />
              Download
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}
